import React, { useCallback, useEffect, useState } from 'react';
import { MessageCircle, X, AlertTriangle, ImagePlus } from 'lucide-react';
import { API_BASE, uploadsPublicUrl } from '../config/api';

const RATINGS = [
  { value: 1, label: 'Péssimo' },
  { value: 2, label: 'Ruim' },
  { value: 3, label: 'Regular' },
  { value: 4, label: 'Bom' },
  { value: 5, label: 'Excelente' },
];

const STATUS_LABEL = {
  received: { label: 'Recebido', className: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
  in_progress: { label: 'Em andamento', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  done: { label: 'Concluído', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
};

const MAX_PRINTS = 4;

function statusMeta(status) {
  return STATUS_LABEL[status] || STATUS_LABEL.received;
}

/**
 * Botão flutuante de feedback, prints e respostas do Master.
 * @param {{ authToken: string, pageContext: string, layoutId?: string|null }} props
 */
export default function FeedbackFloatingWidget({ authToken, pageContext, layoutId = null }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${authToken}` }),
    [authToken],
  );

  const loadMine = useCallback(async () => {
    if (!authToken) return;
    try {
      const r = await fetch(`${API_BASE}/feedback/mine`, { headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return;
      setItems(d.items || []);
      setUnread(Number(d.unread) || 0);
    } catch {
      /* sem rede: o botão continua disponível para um novo envio */
    }
  }, [authToken, authHeaders]);

  useEffect(() => {
    loadMine();
    const onFocus = () => loadMine();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadMine]);

  const resetForm = useCallback(() => {
    setRating(null);
    setComment('');
    setFiles((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.preview));
      return [];
    });
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setMessage(null);
    resetForm();
  }, [resetForm]);

  const markSeen = useCallback(async () => {
    if (!authToken) return;
    try {
      await fetch(`${API_BASE}/feedback/seen`, { method: 'POST', headers: authHeaders() });
      setUnread(0);
      setItems((prev) => prev.map((it) => ({ ...it, unread: false })));
    } catch {
      /* a resposta continua visível na próxima abertura */
    }
  }, [authToken, authHeaders]);

  const openModal = async () => {
    setOpen(true);
    if (!authToken) return;
    try {
      const r = await fetch(`${API_BASE}/feedback/mine`, { headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return;
      const list = d.items || [];
      setItems(list);
      const n = Number(d.unread) || 0;
      setUnread(n);
      if (n > 0) markSeen();
    } catch {
      /* o formulário de envio continua disponível */
    }
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    setFiles((prev) => {
      const room = MAX_PRINTS - prev.length;
      if (room <= 0) {
        setMessage({ type: 'err', text: `No máximo ${MAX_PRINTS} imagens.` });
        return prev;
      }
      const accepted = picked.slice(0, room).map((file) => ({ file, preview: URL.createObjectURL(file) }));
      if (picked.length > room) setMessage({ type: 'err', text: `No máximo ${MAX_PRINTS} imagens.` });
      return [...prev, ...accepted];
    });
  };

  const submit = async () => {
    if (!rating) {
      setMessage({ type: 'err', text: 'Selecione uma nota de 1 a 5.' });
      return;
    }
    if (!authToken) return;
    setSending(true);
    setMessage(null);
    try {
      const body = new FormData();
      body.append('rating', String(rating));
      body.append('comment', comment.trim());
      body.append('page_context', pageContext || '');
      if (layoutId) body.append('layout_id', String(layoutId));
      files.forEach((item) => body.append('images', item.file));
      const r = await fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers: authHeaders(),
        body,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMessage({ type: 'err', text: d.error || 'Não foi possível enviar.' });
        setSending(false);
        return;
      }
      setMessage({ type: 'ok', text: 'Recebemos seu relato. O status fica aqui neste painel.' });
      resetForm();
      await loadMine();
    } catch {
      setMessage({ type: 'err', text: 'Erro de conexão. Tente de novo.' });
    }
    setSending(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="fixed bottom-5 right-5 z-[1940] flex items-center gap-2 rounded-full bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="relative">
          <MessageCircle size={18} aria-hidden />
          {unread > 0 && (
            <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </span>
        {unread > 0 ? 'Nova resposta' : 'Enviar feedback'}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[1950] flex items-end justify-center bg-slate-900/50 p-4 pb-8 backdrop-blur-sm sm:items-center sm:pb-4"
          role="presentation"
          onClick={(e) => e.target === e.currentTarget && close()}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-widget-title"
            className="relative max-h-[min(88vh,760px)] w-full max-w-md overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-600 dark:bg-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={close}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
              aria-label="Fechar"
            >
              <X size={16} />
            </button>

            <h2 id="feedback-widget-title" className="pr-10 text-base font-bold text-[#001d4d] dark:text-slate-100">
              Como você classificaria sua experiência?
            </h2>

            {items.length > 0 && (
              <ul className="mt-4 space-y-2">
                {items.map((item) => {
                  const st = statusMeta(item.status);
                  return (
                    <li key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-900/40">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Nota {item.rating}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${st.className}`}>{st.label}</span>
                      </div>
                      {item.comment ? (
                        <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-200">{item.comment}</p>
                      ) : null}
                      {item.images?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {item.images.map((url) => (
                            <a key={url} href={uploadsPublicUrl(url)} target="_blank" rel="noreferrer">
                              <img src={uploadsPublicUrl(url)} alt="Print enviado" className="h-14 w-14 rounded-md object-cover ring-1 ring-slate-200 dark:ring-slate-600" />
                            </a>
                          ))}
                        </div>
                      )}
                      {item.admin_reply ? (
                        <p className="mt-2 rounded-lg bg-white px-2 py-1.5 text-sm text-slate-800 dark:bg-slate-800 dark:text-slate-100">
                          <span className="font-semibold">Resposta: </span>
                          {item.admin_reply}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}

            <p className="mt-4 text-xs font-semibold text-slate-500 dark:text-slate-400">Novo relato</p>
            <div className="mt-2 flex gap-1.5">
              {RATINGS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRating(r.value)}
                  title={r.label}
                  className={`flex h-9 flex-1 items-center justify-center rounded-lg text-sm font-bold transition ${
                    rating === r.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600'
                  }`}
                  aria-pressed={rating === r.value}
                  aria-label={r.label}
                >
                  {r.value}
                </button>
              ))}
            </div>
            <div className="mt-1 flex justify-between text-[10px] text-slate-400">
              <span>Péssimo</span>
              <span>Excelente</span>
            </div>

            <label className="mt-3 block text-xs font-semibold text-slate-600 dark:text-slate-400" htmlFor="feedback-comment">
              Comentário (opcional)
            </label>
            <textarea
              id="feedback-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={8000}
              placeholder="Conte o que aconteceu ou a melhoria que você sugere..."
              className="mt-1 w-full resize-y rounded-xl border border-blue-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-blue-800 dark:bg-slate-900 dark:text-slate-100"
            />

            <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
              <ImagePlus size={16} aria-hidden />
              Prints (até {MAX_PRINTS}, a imagem é reduzida)
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="sr-only" onChange={onPickFiles} />
            </label>
            {files.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-2">
                {files.map((item, idx) => (
                  <li key={item.preview} className="relative">
                    <img src={item.preview} alt="" className="h-14 w-14 rounded-md object-cover ring-1 ring-slate-200" />
                    <button
                      type="button"
                      className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-slate-800 text-white"
                      aria-label="Remover imagem"
                      onClick={() => setFiles((prev) => {
                        const target = prev[idx];
                        if (target) URL.revokeObjectURL(target.preview);
                        return prev.filter((_, i) => i !== idx);
                      })}
                    >
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {message && (
              <p className={`mt-3 text-sm font-medium ${message.type === 'ok' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                {message.text}
              </p>
            )}

            <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-3 dark:border-slate-600 sm:flex-row sm:items-center sm:justify-between">
              <p className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-amber-500" aria-hidden />
                Este feedback é para melhoria do sistema. A resposta aparece aqui.
              </p>
              <button
                type="button"
                disabled={sending}
                onClick={submit}
                className="shrink-0 rounded-xl bg-sky-500 px-5 py-2 text-sm font-bold text-white shadow transition hover:bg-sky-600 disabled:opacity-60 dark:bg-sky-600 dark:hover:bg-sky-500"
              >
                {sending ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
